import { createFileRoute, useRouter } from '@tanstack/react-router';
import { AlertCircle, ChevronDown, Loader2, Plus, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { z } from 'zod';

import { OrganizationFormModal } from '@apps/web/components/admin/organization_form_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { Input } from '@apps/web/components/ui/input';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { AddToChatButton } from '@apps/web/features/admin_agent_dock/add_to_chat_button';
import { useResolveOrganizationChip } from '@apps/web/features/admin_agent_dock/use_chip_resolvers';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { cn } from '@apps/web/lib/utils';
import { FeatureFlagsTab } from '@apps/web/routes/_app/admin/organizations/feature_flags_tab';
import {
    useAdminPhoneNumberInventory,
    useAdminTwilioA2pRegistrations,
    useAllAdminOrganizations,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type PhoneNumberInventoryItem,
    type TwilioA2pProvisioningOrganization,
} from '@libraries/typescript/api_client/schema_types';

const searchSchema = z.object({
    tab: z
        .enum(['organizations', 'feature-flags', 'twilio-provisioning'])
        .default('organizations'),
    flagKey: z.string().optional(),
});

type AdminOrganizationsTab = z.infer<typeof searchSchema>['tab'];

// Round-7 P2: rendering the "+ add to chat" affordance on rows that
// `useResolveOrganizationChip` cannot resolve produces broken clicks
// that only ever surface as a generic error toast. Two static
// conditions guarantee rejection: (a) no linked customer account —
// the resolver has no summary to read; (b) the customer account is
// shared across multiple organizations — the account summary's
// singular `organization_context` matches at most one row, so the
// others always mismatch. We over-hide the affordance on the primary
// row in a shared account (one working button lost) to eliminate the
// broken siblings; an org-keyed summary provider (option (a) in the
// review) would restore that button but is out of this PR's scope.
export function canResolveOrganizationChip(org: {
    readonly customer_account?: {
        readonly id: string;
        readonly name: string;
        readonly organization_count: number;
    } | null;
}): boolean {
    if (!org.customer_account) {
        return false;
    }
    return org.customer_account.organization_count <= 1;
}

export const Route = createFileRoute('/_app/admin/organizations/')({
    head: () => ({
        meta: [
            {
                title: 'Organizations | Phoebe Admin',
            },
        ],
    }),
    validateSearch: searchSchema,
    component: AdminOrganizationsPage,
});

function AdminOrganizationsPage() {
    const router = useRouter();
    const navigate = Route.useNavigate();
    const { tab, flagKey } = Route.useSearch();
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 300);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const {
        data: twilioProvisioningOrganizations = [],
        isLoading: isLoadingTwilioProvisioning,
    } = useAdminTwilioA2pRegistrations();

    const {
        organizations: allOrganizations,
        isLoading: isLoadingAll,
        isLoadingMore,
        isReachingEnd,
        total: allTotal,
        mutate: mutateAllOrganizations,
    } = useAllAdminOrganizations({
        search: tab === 'organizations' ? debouncedSearch : undefined,
        autoLoadPages: true,
        orgDataOnly: true,
    });

    return (
        <div className="flex h-full flex-col">
            <div className="border-border border-b p-4">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Organizations</h2>
                    <div className="flex items-center gap-4">
                        <div className="text-muted-foreground text-sm">
                            {allTotal} total
                        </div>
                        <Button
                            onClick={() => setIsCreateModalOpen(true)}
                            size="sm"
                            className="gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Create Organization
                        </Button>
                    </div>
                </div>
                <Tabs
                    value={tab}
                    onValueChange={(value) => {
                        void navigate({
                            search: {
                                tab: value as AdminOrganizationsTab,
                            },
                        });
                    }}
                >
                    <TabsList>
                        <TabsTrigger value="organizations">
                            Organizations
                        </TabsTrigger>
                        <TabsTrigger value="feature-flags">
                            Feature Flags
                        </TabsTrigger>
                        <TabsTrigger value="twilio-provisioning">
                            Twilio Provisioning
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>
            {tab === 'organizations' ? (
                <OrganizationsTab
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    organizations={allOrganizations}
                    isLoading={isLoadingAll}
                    isLoadingMore={isLoadingMore}
                    isReachingEnd={isReachingEnd}
                    onRowClick={(orgId) => {
                        router.navigate({
                            to: '/admin/organizations/$organization-id',
                            params: { 'organization-id': orgId },
                        });
                    }}
                />
            ) : tab === 'feature-flags' ? (
                <FeatureFlagsTab
                    organizations={allOrganizations}
                    isLoading={isLoadingAll}
                    isLoadingMore={isLoadingMore ?? false}
                    isReachingEnd={isReachingEnd ?? false}
                    total={allTotal}
                    selectedFlagKey={flagKey}
                    onSelectFlag={(nextFlagKey) => {
                        void navigate({
                            search: (prev) => ({
                                ...prev,
                                flagKey: nextFlagKey,
                            }),
                        });
                    }}
                    onMutate={mutateAllOrganizations}
                />
            ) : (
                <TwilioProvisioningTab
                    organizations={twilioProvisioningOrganizations}
                    isLoading={isLoadingTwilioProvisioning}
                    onOrgClick={(orgId) => {
                        router.navigate({
                            to: '/admin/organizations/$organization-id',
                            params: { 'organization-id': orgId },
                        });
                    }}
                />
            )}
            <OrganizationFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
            />
        </div>
    );
}

export function OrganizationsTab({
    searchQuery,
    setSearchQuery,
    organizations,
    isLoading,
    isLoadingMore,
    isReachingEnd,
    onRowClick,
}: {
    readonly searchQuery: string;
    readonly setSearchQuery: (query: string) => void;
    readonly organizations: ReadonlyArray<{
        readonly id: string;
        readonly name: string;
        readonly internal_name?: string | null;
        readonly created_at: string;
        readonly timezone: string;
        readonly ehr_type?: string | null;
        readonly ehr_connection_type?: string | null;
        readonly customer_account?: {
            readonly id: string;
            readonly name: string;
            readonly organization_count: number;
        } | null;
    }>;
    readonly isLoading: boolean;
    readonly isLoadingMore: boolean;
    readonly isReachingEnd: boolean;
    readonly onRowClick: (orgId: string) => void;
}) {
    const router = useRouter();
    const resolveOrganizationChip = useResolveOrganizationChip();
    const getOrganizationNavigation = (orgId: string) => {
        const routeOptions = {
            to: '/admin/organizations/$organization-id',
            params: { 'organization-id': orgId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => onRowClick(orgId),
        };
    };

    return (
        <ScrollArea className="flex-1 overflow-auto">
            <div className="p-4 pb-2">
                <div className="relative">
                    <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
                    <Input
                        placeholder="Search organizations…"
                        className="pl-9"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>
            {isLoading ? (
                <div className="flex h-32 items-center justify-center">
                    <div className="text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading organizations…</span>
                    </div>
                </div>
            ) : organizations?.length === 0 ? (
                <div className="flex h-32 items-center justify-center">
                    <div className="text-muted-foreground text-sm">
                        {searchQuery
                            ? 'No organizations found'
                            : 'No organizations yet'}
                    </div>
                </div>
            ) : (
                <React.Fragment>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Internal Name</TableHead>
                                <TableHead>Customer Account</TableHead>
                                <TableHead>EHR</TableHead>
                                <TableHead>Created At</TableHead>
                                <TableHead>Timezone</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {organizations.map((org) => (
                                <TableRow
                                    key={org.id}
                                    className="group/org-row cursor-pointer"
                                    {...getNavigableElementProps(
                                        getOrganizationNavigation(org.id),
                                    )}
                                >
                                    <TableCell className="font-medium">
                                        <div className="flex items-center gap-1.5">
                                            <span>{org.name}</span>
                                            {canResolveOrganizationChip(org) ? (
                                                <AddToChatButton
                                                    kind="organization"
                                                    entityId={org.id}
                                                    resolveChip={() =>
                                                        resolveOrganizationChip(
                                                            {
                                                                accountId:
                                                                    org
                                                                        .customer_account
                                                                        ?.id ??
                                                                    null,
                                                                organizationId:
                                                                    org.id,
                                                                fallbackLabel:
                                                                    org.name,
                                                            },
                                                        )
                                                    }
                                                    className="opacity-0 group-hover/org-row:opacity-100 data-[add-state=added]:opacity-100 data-[add-state=duplicate]:opacity-100 data-[add-state=pending]:opacity-100"
                                                />
                                            ) : null}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {org.internal_name ?? (
                                            <span className="text-muted-foreground">
                                                Not set
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {org.customer_account ? (
                                            <div className="flex items-center gap-2">
                                                <span>
                                                    {org.customer_account.name}
                                                </span>
                                                {org.customer_account
                                                    .organization_count > 1 ? (
                                                    <Badge variant="secondary">
                                                        {
                                                            org.customer_account
                                                                .organization_count
                                                        }{' '}
                                                        orgs
                                                    </Badge>
                                                ) : null}
                                            </div>
                                        ) : (
                                            <span className="text-muted-foreground">
                                                Not assigned
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {org.ehr_connection_type ||
                                        org.ehr_type ? (
                                            formatEhrLabel(
                                                org.ehr_connection_type ??
                                                    org.ehr_type ??
                                                    '',
                                            )
                                        ) : (
                                            <span className="text-muted-foreground">
                                                Not configured
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {new Date(
                                            org.created_at,
                                        ).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>{org.timezone}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {isLoadingMore && !isReachingEnd ? (
                        <div className="flex h-12 items-center justify-center">
                            <div className="text-muted-foreground flex items-center gap-2 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading more…</span>
                            </div>
                        </div>
                    ) : null}
                </React.Fragment>
            )}
        </ScrollArea>
    );
}

function formatEhrLabel(value: string): string {
    const labels: Record<string, string> = {
        axiscare: 'AxisCare',
        generations_homecare: 'Generations Homecare',
        hhaexchange: 'HHAeXchange',
        wellsky: 'WellSky',
    };
    return labels[value] ?? value;
}

type TwilioProvisioningRegistration =
    TwilioA2pProvisioningOrganization['registration'];

const TWILIO_PROVISIONING_STALE_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;

function TwilioProvisioningTab({
    organizations,
    isLoading,
    onOrgClick,
}: {
    readonly organizations: ReadonlyArray<TwilioA2pProvisioningOrganization>;
    readonly isLoading: boolean;
    readonly onOrgClick: (orgId: string) => void;
}) {
    const { errors, stale, inProgress, incompleteWithPhoneNumber, completed } =
        useMemo(() => {
            const attentionNeeded: Array<TwilioA2pProvisioningOrganization> =
                [];
            const staleRegistrations: Array<TwilioA2pProvisioningOrganization> =
                [];
            const active: Array<TwilioA2pProvisioningOrganization> = [];
            const skippedWithPhoneNumber: Array<TwilioA2pProvisioningOrganization> =
                [];
            const done: Array<TwilioA2pProvisioningOrganization> = [];
            const nowMs = Date.now();

            for (const organization of organizations) {
                if (isTwilioProvisioningComplete(organization.registration)) {
                    done.push(organization);
                } else if (organization.has_phone_number) {
                    skippedWithPhoneNumber.push(organization);
                } else if (
                    hasTwilioProvisioningError(organization.registration)
                ) {
                    attentionNeeded.push(organization);
                } else if (
                    isTwilioProvisioningStale(organization.registration, nowMs)
                ) {
                    staleRegistrations.push(organization);
                } else {
                    active.push(organization);
                }
            }

            attentionNeeded.sort(compareTwilioProvisioningUpdatedDescending);
            staleRegistrations.sort(compareTwilioProvisioningUpdatedDescending);
            active.sort(compareTwilioProvisioningUpdatedDescending);
            skippedWithPhoneNumber.sort(
                compareTwilioProvisioningUpdatedDescending,
            );
            done.sort(compareTwilioProvisioningUpdatedDescending);

            return {
                errors: attentionNeeded,
                stale: staleRegistrations,
                inProgress: active,
                incompleteWithPhoneNumber: skippedWithPhoneNumber,
                completed: done,
            };
        }, [organizations]);

    if (isLoading) {
        return (
            <div className="flex h-32 items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading Twilio provisioning…</span>
                </div>
            </div>
        );
    }

    if (organizations.length === 0) {
        return (
            <ScrollArea className="flex-1 overflow-auto">
                <div className="space-y-5 p-4">
                    <div className="text-muted-foreground py-8 text-center text-sm">
                        No Twilio A2P registrations found.
                    </div>
                    <PhoneNumberPoolSection />
                </div>
            </ScrollArea>
        );
    }

    return (
        <ScrollArea className="flex-1 overflow-auto">
            <div className="space-y-5 p-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                    <TwilioProvisioningSummary
                        label="Errors"
                        value={errors.length}
                    />
                    <TwilioProvisioningSummary
                        label="In progress"
                        value={inProgress.length}
                    />
                    <TwilioProvisioningSummary
                        label="Stale"
                        value={stale.length}
                    />
                    <TwilioProvisioningSummary
                        label="With number"
                        value={incompleteWithPhoneNumber.length}
                    />
                    <TwilioProvisioningSummary
                        label="Completed"
                        value={completed.length}
                    />
                    <TwilioProvisioningSummary
                        label="Total"
                        value={organizations.length}
                    />
                </div>
                <TwilioProvisioningTable
                    title="Errors"
                    organizations={errors}
                    emptyText="No organizations need attention."
                    onOrgClick={onOrgClick}
                    defaultOpen={true}
                />
                <TwilioProvisioningTable
                    title="In progress"
                    organizations={inProgress}
                    emptyText="No organizations are currently in progress."
                    onOrgClick={onOrgClick}
                    defaultOpen={true}
                />
                <TwilioProvisioningTable
                    title="Incomplete with phone number"
                    description="Provisioning isn’t required because these organizations already have a phone number."
                    organizations={incompleteWithPhoneNumber}
                    emptyText="No incomplete registrations already have phone numbers."
                    onOrgClick={onOrgClick}
                    defaultOpen={true}
                />
                <TwilioProvisioningTable
                    title="Stale"
                    organizations={stale}
                    emptyText="No incomplete registrations are stale."
                    onOrgClick={onOrgClick}
                    defaultOpen={false}
                />
                <TwilioProvisioningTable
                    title="Completed"
                    organizations={completed}
                    emptyText="No completed registrations yet."
                    onOrgClick={onOrgClick}
                    defaultOpen={false}
                />
                <PhoneNumberPoolSection />
            </div>
        </ScrollArea>
    );
}

// Ordered so the summary line reads pool-health first: what's ready to hand
// out, then in-flight claims, then already-consumed and retired numbers.
const PHONE_NUMBER_POOL_STATUSES = [
    'available',
    'reserved',
    'allocated',
    'released',
] as const;

function PhoneNumberPoolSection() {
    const { data: numbers = [], isLoading } = useAdminPhoneNumberInventory();
    const [open, setOpen] = useState(true);

    const statusCounts = useMemo(() => {
        const counts = new Map<string, number>();
        for (const number of numbers) {
            counts.set(number.status, (counts.get(number.status) ?? 0) + 1);
        }
        return counts;
    }, [numbers]);

    const summary = PHONE_NUMBER_POOL_STATUSES.filter((status) =>
        statusCounts.has(status),
    )
        .map((status) => `${statusCounts.get(status)} ${status}`)
        .join(' · ');

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <div className="border-border rounded-md border">
                <CollapsibleTrigger asChild={true}>
                    <Button
                        type="button"
                        variant="ghost"
                        className="hover:bg-muted flex h-auto w-full items-center justify-between rounded-none px-4 py-3"
                    >
                        <div className="text-left">
                            <div className="text-sm font-semibold">
                                Phone Number Pool
                            </div>
                            <div className="text-muted-foreground text-xs">
                                {numbers.length} numbers
                                {summary ? ` — ${summary}` : ''}
                            </div>
                            <div className="text-muted-foreground mt-1 text-xs">
                                Numbers bought ahead of time by the pool
                                collector, held until an organization claims one
                                during provisioning.
                            </div>
                        </div>
                        <ChevronDown
                            className={cn(
                                'h-4 w-4 transition-transform',
                                open ? 'rotate-180' : '',
                            )}
                        />
                    </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <PhoneNumberPoolTableBody
                        numbers={numbers}
                        isLoading={isLoading}
                    />
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}

function PhoneNumberPoolTableBody({
    numbers,
    isLoading,
}: {
    readonly numbers: ReadonlyArray<PhoneNumberInventoryItem>;
    readonly isLoading: boolean;
}) {
    if (isLoading) {
        return (
            <div className="text-muted-foreground flex items-center justify-center gap-2 px-4 py-8 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading phone number pool…</span>
            </div>
        );
    }

    if (numbers.length === 0) {
        return (
            <div className="text-muted-foreground px-4 py-8 text-center text-sm">
                No pooled phone numbers yet.
            </div>
        );
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Area Code</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Allocated To</TableHead>
                    <TableHead>Purchased</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {numbers.map((number) => (
                    <TableRow key={number.id}>
                        <TableCell className="font-medium">
                            {number.phone_number}
                        </TableCell>
                        <TableCell>{number.area_code}</TableCell>
                        <TableCell>
                            <PhoneNumberPoolStatusBadge
                                status={number.status}
                            />
                        </TableCell>
                        <TableCell>
                            {number.allocated_organization_name ?? (
                                <span className="text-muted-foreground">—</span>
                            )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                            {formatTwilioProvisioningDate(number.created_at)}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function PhoneNumberPoolStatusBadge({
    status,
}: {
    readonly status: PhoneNumberInventoryItem['status'];
}) {
    switch (status) {
        case 'available':
            return <Badge variant="success">Available</Badge>;
        case 'reserved':
            return <Badge variant="outline">Reserved</Badge>;
        case 'allocated':
            return <Badge variant="secondary">Allocated</Badge>;
        case 'released':
            return <Badge variant="destructive">Released</Badge>;
        default:
            return <Badge variant="outline">{status}</Badge>;
    }
}

function TwilioProvisioningSummary({
    label,
    value,
}: {
    readonly label: string;
    readonly value: number;
}) {
    return (
        <div className="border-border rounded-md border px-4 py-3">
            <div className="text-muted-foreground text-xs font-medium">
                {label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">
                {value}
            </div>
        </div>
    );
}

function TwilioProvisioningTable({
    title,
    organizations,
    emptyText,
    description,
    onOrgClick,
    defaultOpen,
}: {
    readonly title: string;
    readonly organizations: ReadonlyArray<TwilioA2pProvisioningOrganization>;
    readonly emptyText: string;
    readonly description?: string;
    readonly onOrgClick: (orgId: string) => void;
    readonly defaultOpen: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <div className="border-border rounded-md border">
                <CollapsibleTrigger asChild={true}>
                    <Button
                        type="button"
                        variant="ghost"
                        className="hover:bg-muted flex h-auto w-full items-center justify-between rounded-none px-4 py-3"
                    >
                        <div className="text-left">
                            <div className="text-sm font-semibold">{title}</div>
                            <div className="text-muted-foreground text-xs">
                                {organizations.length} organizations
                            </div>
                            {description ? (
                                <div className="text-muted-foreground mt-1 text-xs">
                                    {description}
                                </div>
                            ) : null}
                        </div>
                        <ChevronDown
                            className={cn(
                                'h-4 w-4 transition-transform',
                                open ? 'rotate-180' : '',
                            )}
                        />
                    </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <TwilioProvisioningTableBody
                        organizations={organizations}
                        emptyText={emptyText}
                        onOrgClick={onOrgClick}
                    />
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
}

function TwilioProvisioningTableBody({
    organizations,
    emptyText,
    onOrgClick,
}: {
    readonly organizations: ReadonlyArray<TwilioA2pProvisioningOrganization>;
    readonly emptyText: string;
    readonly onOrgClick: (orgId: string) => void;
}) {
    const router = useRouter();
    const getOrganizationNavigation = (orgId: string) => {
        const routeOptions = {
            to: '/admin/organizations/$organization-id',
            params: { 'organization-id': orgId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => onOrgClick(orgId),
        };
    };

    if (organizations.length === 0) {
        return (
            <div className="text-muted-foreground px-4 py-8 text-center text-sm">
                {emptyText}
            </div>
        );
    }

    return (
        <Table className="min-w-[1120px]">
            <TableHeader>
                <TableRow>
                    <TableHead className="w-[260px]">Organization</TableHead>
                    <TableHead className="w-[180px] max-w-[180px] whitespace-normal">
                        Current Step
                    </TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>A2P Profile</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead className="w-[180px]">Updated</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {organizations.map((organization) => (
                    <TableRow
                        key={organization.organization_id}
                        className="cursor-pointer"
                        {...getNavigableElementProps(
                            getOrganizationNavigation(
                                organization.organization_id,
                            ),
                        )}
                    >
                        <TableCell>
                            <div className="font-medium">
                                {getTwilioProvisioningOrgLabel(organization)}
                            </div>
                            {organization.organization_internal_name ? (
                                <div className="text-muted-foreground text-xs">
                                    {organization.organization_name}
                                </div>
                            ) : null}
                            {organization.legal_business_name ? (
                                <div className="text-muted-foreground text-xs">
                                    Legal: {organization.legal_business_name}
                                </div>
                            ) : null}
                        </TableCell>
                        <TableCell className="w-[180px] max-w-[180px] whitespace-normal">
                            <div className="flex max-w-[180px] flex-col gap-1 break-words whitespace-normal">
                                <span className="font-medium">
                                    {getTwilioProvisioningStep(
                                        organization.registration,
                                    )}
                                </span>
                                {organization.registration.last_error ? (
                                    <TwilioProvisioningErrorTooltip
                                        error={
                                            organization.registration.last_error
                                        }
                                    />
                                ) : null}
                            </div>
                        </TableCell>
                        <TableCell>
                            <TwilioApprovalBadge
                                status={
                                    organization.registration
                                        .customer_profile_approval_status
                                }
                            />
                        </TableCell>
                        <TableCell>
                            <TwilioApprovalBadge
                                status={
                                    organization.registration
                                        .a2p_messaging_profile_approval_status
                                }
                            />
                        </TableCell>
                        <TableCell>
                            <TwilioApprovalBadge
                                status={
                                    organization.registration
                                        .brand_registration_approval_status
                                }
                            />
                        </TableCell>
                        <TableCell>
                            <TwilioApprovalBadge
                                status={
                                    organization.registration
                                        .campaign_approval_status
                                }
                            />
                        </TableCell>
                        <TableCell>
                            <Badge
                                variant={
                                    organization.registration
                                        .messaging_service_sid
                                        ? 'success'
                                        : 'secondary'
                                }
                            >
                                {organization.registration.messaging_service_sid
                                    ? 'Created'
                                    : 'Missing'}
                            </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                            {formatTwilioProvisioningDate(
                                organization.registration.updated_at,
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function TwilioProvisioningErrorTooltip({ error }: { readonly error: string }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <button
                    type="button"
                    className="text-destructive hover:bg-destructive/10 inline-flex w-fit items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium"
                    aria-label="View Twilio provisioning error"
                    onClick={(event) => event.stopPropagation()}
                >
                    <AlertCircle className="h-3.5 w-3.5" />
                    Error
                </button>
            </TooltipTrigger>
            <TooltipContent
                side="right"
                align="start"
                className="max-w-[420px] text-left leading-relaxed break-words whitespace-pre-wrap"
            >
                {error}
            </TooltipContent>
        </Tooltip>
    );
}

function TwilioApprovalBadge({ status }: { readonly status?: string | null }) {
    if (!status) {
        return <Badge variant="secondary">Not started</Badge>;
    }

    if (status === 'approved') {
        return <Badge variant="success">Approved</Badge>;
    }

    if (status === 'rejected') {
        return <Badge variant="destructive">Rejected</Badge>;
    }

    return <Badge variant="outline">{formatApprovalStatus(status)}</Badge>;
}

function compareTwilioProvisioningUpdatedDescending(
    left: TwilioA2pProvisioningOrganization,
    right: TwilioA2pProvisioningOrganization,
): number {
    const updatedDiff =
        new Date(right.registration.updated_at).getTime() -
        new Date(left.registration.updated_at).getTime();
    if (updatedDiff !== 0) {
        return updatedDiff;
    }
    return compareTwilioProvisioningOrgLabel(left, right);
}

function compareTwilioProvisioningOrgLabel(
    left: TwilioA2pProvisioningOrganization,
    right: TwilioA2pProvisioningOrganization,
): number {
    return getTwilioProvisioningOrgLabel(left).localeCompare(
        getTwilioProvisioningOrgLabel(right),
    );
}

function getTwilioProvisioningOrgLabel(
    organization: TwilioA2pProvisioningOrganization,
): string {
    const internal = organization.organization_internal_name?.trim();
    return internal && internal.length > 0
        ? internal
        : organization.organization_name;
}

function isTwilioProvisioningComplete(
    registration: TwilioProvisioningRegistration,
): boolean {
    return (
        registration.customer_profile_approval_status === 'approved' &&
        registration.brand_registration_approval_status === 'approved' &&
        registration.campaign_approval_status === 'approved' &&
        Boolean(registration.messaging_service_sid)
    );
}

function getTwilioProvisioningStep(
    registration: TwilioProvisioningRegistration,
): string {
    if (hasTwilioProvisioningError(registration)) {
        return 'Needs attention';
    }
    if (registration.customer_profile_approval_status !== 'approved') {
        return 'Customer Profile';
    }
    if (
        registration.a2p_messaging_profile_approval_status !== 'approved' &&
        registration.a2p_messaging_profile_approval_status !== 'pending'
    ) {
        return 'A2P Messaging Profile';
    }
    if (registration.brand_registration_approval_status !== 'approved') {
        return 'Brand Registration';
    }
    if (!registration.messaging_service_sid) {
        return 'Messaging Service';
    }
    if (registration.campaign_approval_status !== 'approved') {
        return 'Campaign';
    }
    return 'Complete';
}

function hasTwilioProvisioningError(
    registration: TwilioProvisioningRegistration,
): boolean {
    return (
        Boolean(registration.last_error) || hasRejectedTwilioStep(registration)
    );
}

function isTwilioProvisioningStale(
    registration: TwilioProvisioningRegistration,
    nowMs: number,
): boolean {
    return (
        nowMs - new Date(registration.updated_at).getTime() >=
        TWILIO_PROVISIONING_STALE_THRESHOLD_MS
    );
}

function hasRejectedTwilioStep(
    registration: TwilioProvisioningRegistration,
): boolean {
    return [
        registration.customer_profile_approval_status,
        registration.a2p_messaging_profile_approval_status,
        registration.brand_registration_approval_status,
        registration.campaign_approval_status,
    ].includes('rejected');
}

function formatApprovalStatus(status: string): string {
    return status
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function formatTwilioProvisioningDate(value: string): string {
    return new Date(value).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
